"use client";

import React from "react";
import Image from "next/image";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { PageTrademania2Schema } from "@/sanity/pages/PageTrademania2Template";
import {
  Chip,
  FadeUp,
  Highlight,
  RevealWords,
  pad,
  revealLines,
} from "@/components/trademania-2/shared";

export default function Host({ data }: { data: PageTrademania2Schema }) {
  return (
    <section className="relative px-[2rem] pb-[8rem] pt-[7rem] lg:px-0 lg:pt-0 lg:pb-[16.5rem]">
      {/* phones: chip and title centred, then photo, copy, facts, CTA in one column, 25.33px apart
          (Figma spreads them over the 1427px frame); desktop: copy left, photo right */}
      <div className="mx-auto flex w-full max-w-[150rem] flex-col gap-[3.1667rem] lg:grid lg:grid-cols-[654.531fr_488fr] lg:items-center lg:gap-[7.125rem]">
        <div className="contents lg:flex lg:flex-col lg:items-start lg:gap-[4rem]">
          <div className="contents lg:flex lg:w-full lg:flex-col lg:items-start lg:gap-[3rem]">
            <div className="order-1 flex flex-col items-center gap-[3.1667rem] lg:items-start lg:gap-[3rem]">
              <Chip text={data?.hostChip} slashes />
              <h2
                ref={revealLines}
                data-reveal
                className="relative z-0 text-center font-owners text-[4.5rem] font-medium uppercase leading-[1.055] tracking-[.01em] text-black lg:inline-flex lg:flex-col lg:whitespace-nowrap lg:text-left lg:text-[7.5rem] lg:leading-[1.08] lg:tracking-[.02em]">
                {/* phones and desktop box the whole second line */}
                <span className="lg:hidden">
                  <RevealWords text={data?.hostTitle} />
                  <br />
                  {data?.hostTitleHighlight && (
                    <Highlight>
                      <RevealWords text={data.hostTitleHighlight} />
                    </Highlight>
                  )}
                </span>
                <span className="tm2-trim-start hidden lg:block">
                  <RevealWords text={data?.hostTitle} />
                </span>
                {data?.hostTitleHighlight && (
                  <span className="tm2-trim-end relative hidden lg:block lg:self-start">
                    {/* box hugs the highlighted line, 12px past it on both sides */}
                    <span
                      aria-hidden
                      className="absolute inset-x-[-1.5rem] bottom-[-.23em] top-[-.28em] -z-10 border-x-[3px] border-[#9BE055] bg-white"
                    />
                    <RevealWords text={data.hostTitleHighlight} />
                  </span>
                )}
              </h2>
            </div>
            {data?.hostText && (
              <FadeUp className="order-3 w-full">
                <p className="font-geist text-[2.25rem] font-medium leading-[1.4] tracking-[-.02em] text-black/60 lg:w-[82.5rem] lg:whitespace-pre-line lg:tracking-[-.03em]">
                  {data.hostText}
                </p>
              </FadeUp>
            )}
          </div>

          {!!data?.hostFacts?.length && (
            <ul className="order-4 flex w-full flex-col gap-[1rem] lg:-mt-[1rem] lg:pt-px">
              {data.hostFacts.map((fact, i) => (
                <li
                  key={i}
                  className="flex w-full items-start gap-[1.5rem] rounded-[3px] bg-black/[.04] py-[1rem] pl-[1rem] pr-[1.5rem] lg:items-center lg:gap-[1.75rem] lg:p-[1rem]"
                >
                  <span className="flex h-[5rem] w-[5.5rem] shrink-0 items-center justify-center rounded-[2px] bg-white/80 font-owners text-[2.25rem] font-medium leading-none text-[#8BCF47] lg:h-auto lg:min-h-[5.375rem] lg:w-[6.375rem] lg:self-stretch lg:text-[2.75rem] lg:leading-[3rem]">
                    {pad(i + 1)}.
                  </span>
                  <p className="font-geist text-[2.25rem] font-medium leading-[1.4] tracking-[-.02em] text-black/60 lg:tracking-[-.03em]">
                    {fact?.lead && (
                      <>
                        <span className="text-black/80">{fact.lead}</span> <br className="hidden lg:inline" />
                      </>
                    )}
                    {fact?.text}
                  </p>
                </li>
              ))}
            </ul>
          )}

          {data?.hostCTA && (
            <FadeUp className="order-5 w-full lg:w-auto">
              <CTACornerButton data={data.hostCTA} />
            </FadeUp>
          )}
        </div>

        <figure className="order-2 flex flex-col gap-[3rem] lg:h-[92.5rem] lg:justify-between lg:gap-[2.5rem]">
          <div className="relative h-[50rem] w-full overflow-hidden rounded-[6px] bg-black lg:aspect-[488/695] lg:h-auto">
            {/* both photos are pre-cropped Figma exports and fill the frame as they are */}
            {data?.hostImage?.src && (
              <Image
                src={data.hostImage}
                alt={data?.hostName ?? ""}
                sizes="35vw"
                className={`absolute inset-0 h-full w-full object-cover ${data?.hostImageMobile?.src ? "hidden lg:block" : ""}`}
              />
            )}
            {data?.hostImageMobile?.src && (
              <Image
                src={data.hostImageMobile}
                alt={data?.hostName ?? ""}
                sizes="100vw"
                className="absolute inset-0 h-full w-full object-cover lg:hidden"
              />
            )}
            <div className="absolute inset-0 bg-black/10" />
          </div>
          <figcaption className="flex h-[3rem] w-full items-center justify-between gap-[1rem] lg:h-auto lg:gap-[2rem]">
            <span className="whitespace-nowrap font-geist text-[2.25rem] font-medium leading-none tracking-[-.02em] text-[#1F231A] lg:text-[3rem] lg:leading-[3rem]">
              {data?.hostName}
            </span>
            <span className="h-px w-[14.75rem] shrink bg-black/[.18]" />
            <span className="whitespace-nowrap font-geist text-[1.75rem] font-medium leading-none tracking-[-.02em] text-black/60 lg:text-[2.25rem] lg:leading-[1.4] lg:tracking-[-.03em]">
              {data?.hostRole}
            </span>
          </figcaption>
        </figure>
      </div>
    </section>
  );
}
